import React, { useMemo } from "react";
import { Dialog, IconButton, IIconProps } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { EcoaImgDisplaySpec, EcoaImgUrlSpec, EcoaPopupTextSpec } from "maven-lib/dist/consts/edc";
import { arrayify, classNames } from "maven-lib/dist/utils/misc";
import { alterElementOnImgErr, StyledImgWrapper } from "./Common.styles";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";

export type EcoaPopUpTextSpecForRender = Omit<EcoaPopupTextSpec, "img"> & { img?: EcoaImgDisplaySpec & EcoaImgUrlSpec };
export type EcoaPopupSize = "LARGE" | "X-LARGE" | "2X-LARGE" | "3X-LARGE" | "4X-LARGE" | "5X-LARGE" | "6X-LARGE" | "7X-LARGE" | "8X-LARGE";

const Anchor = ({ url, color, bold, children }: { url?: string; color?: "GRAY" | "DANGER"; bold?: boolean; children: React.ReactNode }) => {
  return url ? (
    <a
      className={classNames(`--color-${String(color || "default").toLowerCase()}`, bold && "--bold")}
      href={url}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  ) : (
    <>{children}</>
  );
};

export interface EcoaPopupProps {
  title: string;
  texts: Array<EcoaPopUpTextSpecForRender>;
  size?: EcoaPopupSize;
  outLinedIcon?: "help_outline";
}

export function EcoaPopup(props: EcoaPopupProps) {
  const { title, texts, outLinedIcon, size } = props;
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);

  const cancelIcon: IIconProps = { iconName: "Cancel" };

  const element = useMemo(() => {
    return (
      <>
        <div className="app-hybrid-popup-title">{title}</div>
        <div className="app-hybrid-popup-button-inner">
          <IconButton className="ms-Button" iconProps={cancelIcon} ariaLabel="Close popup modal" onClick={hideModal} />
        </div>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  return (
    <>
      <div className="app-hybrid-popup-icon">
        <i className="notranslate material-icons-outlined" onClick={showModal}>
          {outLinedIcon || "help"}
        </i>
      </div>
      <Dialog className="app-hybrid-popup" isOpen={isModalOpen} onDismiss={hideModal} title={element}>
        <div className={"app-hybrid-popup-body"}>
          {arrayify(texts).map((popupText, popupIdx) => (
            <Anchor
              key={popupIdx}
              url={popupText?.link?.url}
              color={((color) => (color === "RED" ? "DANGER" : color === "GRAY" ? "GRAY" : undefined))(popupText.color ?? "BLACK")}
              bold={popupText.bold}
            >
              {popupText.img && popupText.img.position === "TOP" && (
                <StyledImgWrapper>
                  <img
                    alt=""
                    style={{ width: "100%", objectFit: "contain" }}
                    src={popupText.img.url}
                    onError={(e) => (e.currentTarget.outerHTML = alterElementOnImgErr)}
                  />
                </StyledImgWrapper>
              )}
              <HbParagraph
                text={popupText.text}
                size={size}
                align={popupText.align ?? "LEFT"}
                color={((color) => (color === "RED" ? "DANGER" : color === "GRAY" ? "GRAY" : undefined))(popupText.color ?? "BLACK")}
                bold={popupText.bold}
              />
              {popupText.img && popupText.img.position === "BOTTOM" && (
                <StyledImgWrapper>
                  <img
                    alt=""
                    style={{ width: "100%", objectFit: "contain" }}
                    src={popupText.img.url}
                    onError={(e) => (e.currentTarget.outerHTML = alterElementOnImgErr)}
                  />
                </StyledImgWrapper>
              )}
            </Anchor>
          ))}
        </div>
      </Dialog>
    </>
  );
}
